import { Component, OnInit, inject, signal } from '@angular/core';
import { PrintOfficeService } from '../../../services/print-office.service';
import { AuthService } from '../../../services/auth/auth.service';
import { GetPrintOfficeResponseDto } from '../../../models/server-models/reseponse-dtos/get-print-office-response-dto';
import { ErrorService } from '../../../../../shared/services/error.service';
import { PrintOrderDto } from '../../../models/server-models/entities/print-order-dto';
import { PrintOrderService } from '../../../services/print-order.service';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { SpActionButton, SpColumn } from '../../../models/client-models/entities/sp-columns';
import { PrintOrderFileDto } from '../../../models/server-models/entities/print-order-file-dto';
import { PrintOrderStatus } from '../../../models/server-models/enums/print-order-status';
import { FileContentType } from '../../../models/server-models/enums/file-content-type';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Capacitor } from '@capacitor/core';
import { BrowserService } from '../../../services/in-app-services/browser.service';

@Component({
  selector: 'app-user-order-details',
  templateUrl: './user-order-details.page.html',
  styleUrls: ['./user-order-details.page.scss'],
  standalone: false,
})
export class UserOrderDetailsPage implements OnInit {
  printOfficeService = inject(PrintOfficeService);
  authService = inject(AuthService);
  errorService = inject(ErrorService);
  printOrderService = inject(PrintOrderService);
  activatedRoute = inject(ActivatedRoute);
  alertController = inject(AlertController);
  translateService = inject(TranslateService);
  isWeb = Capacitor.getPlatform() === 'web';
  browserService = inject(BrowserService);
  loading: HTMLIonLoadingElement | null = null;

  printOffice: GetPrintOfficeResponseDto = { printOffice: null };

  printOrderId = signal<string | null>(null);
  printOrder = signal<PrintOrderDto | null>(null);

  fileColumns: SpColumn<PrintOrderFileDto>[] = [];
  fileActions: SpActionButton<PrintOrderFileDto>[] = [];

  printOrderStatuses = [
    PrintOrderStatus.Created,
    PrintOrderStatus.InProgress,
    PrintOrderStatus.Completed,
    PrintOrderStatus.Canceled,
    PrintOrderStatus.Collected,
  ];

  constructor() {}

  ngOnInit() {
    this.printOrderId.set(this.activatedRoute.snapshot.paramMap.get('id'));

    this.setupTable();
    this.loadData();
  }

  loadData() {
    this.printOrderService.getOrderForUser(this.printOrderId()!).subscribe({
      next: (order) => {
        this.printOrder.set(order);
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.handleError(error);
      },
    });
  }

  async openGoogleMaps(url: string | undefined) {
    this.browserService.openUrl(url);
  }

  statusAsText(status: PrintOrderStatus) {
    switch (status) {
      case PrintOrderStatus.Created:
        return this.translateService.instant('PRINT.ORDER.STATUS.CREATED');
      case PrintOrderStatus.InProgress:
        return this.translateService.instant('PRINT.ORDER.STATUS.IN_PROGRESS');
      case PrintOrderStatus.Completed:
        return this.translateService.instant('PRINT.ORDER.STATUS.COMPLETED');
      case PrintOrderStatus.Canceled:
        return this.translateService.instant('PRINT.ORDER.STATUS.CANCELED');
      case PrintOrderStatus.Collected:
        return this.translateService.instant('PRINT.ORDER.STATUS.COLLECTED');
    }
  }

  fileTypeAsText(fileType: FileContentType) {
    switch (fileType) {
      case FileContentType.Pdf:
        return 'PDF';
      case FileContentType.Jpeg:
        return 'JPG';
      case FileContentType.Png:
        return 'PNG';
      case FileContentType.Unknown:
        return 'UNKNOWN';
    }
  }

  getStatusCssClass(): string {
    switch (this.printOrder()?.status) {
      case PrintOrderStatus.Created:
        return 'status-created';
      case PrintOrderStatus.InProgress:
        return 'status-in-progress';
      case PrintOrderStatus.Completed:
        return 'status-completed';
      case PrintOrderStatus.Collected:
        return 'status-collected';
      case PrintOrderStatus.Canceled:
        return 'status-canceled';
      default:
        return 'status-created';
    }
  }

  private setupTable() {
    this.fileColumns = [
      {
        name: 'PRINT.FILE.ORIGINAL_NAME',
        propertySelector: (file) => file.originalFileName,
      },
      {
        name: 'PRINT.FILE.PAGES',
        propertySelector: (file) => file.pagesQuantity,
      },
      {
        name: 'PRINT.FILE.COPIES',
        propertySelector: (file) => file.copies,
      },
      {
        name: 'PRINT.FILE.TYPE',
        propertySelector: (file) => this.fileTypeAsText(file.fileType),
      },
    ];
  }
}
